import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';

const i18n = defineMessages({
	accessUpdated: {
		id: 'share-and-restrict-dialog.specific-access-dialog.flags.access-updated',
		defaultMessage: 'Access updated',
		description: 'Success flag title after specific access changes are applied',
	},
	accessChangeFailedTitle: {
		id: 'share-and-restrict-dialog.specific-access-dialog.flags.access-change-failed.title',
		defaultMessage: 'Access change failed',
		description: 'Error flag title when specific access change fails',
	},
	accessChangeFailedDescription: {
		id: 'share-and-restrict-dialog.specific-access-dialog.flags.access-change-failed.description',
		defaultMessage: 'We ran into a problem completing your action. Wait a while and try again.',
		description: 'Error flag description when specific access change fails',
	},
});

export type SpecificAccessFlags = {
	showAccessUpdatedFlag: () => void;
	showAccessChangeFailedFlag: () => void;
};

export function useSpecificAccessFlags({ flags }: WithFlagsProps): SpecificAccessFlags {
	const { formatMessage } = useIntl();
	const { AutoDismissFlag } = useChoreographerAssets();

	const showAccessUpdatedFlag = useCallback(() => {
		void flags.showFlag({
			type: 'success-circle',
			title: formatMessage(i18n.accessUpdated),
			customComponent: (props: any) => (
				<AutoDismissFlag
					{...props}
					messageId="share-and-restrict-dialog-specific-access-success"
					messageType="transactional"
				/>
			),
		});
	}, [AutoDismissFlag, flags, formatMessage]);

	const showAccessChangeFailedFlag = useCallback(() => {
		void flags.showErrorFlag({
			title: formatMessage(i18n.accessChangeFailedTitle),
			description: formatMessage(i18n.accessChangeFailedDescription),
		});
	}, [flags, formatMessage]);

	return { showAccessUpdatedFlag, showAccessChangeFailedFlag };
}
